import React, { useState, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { TextField, InputAdornment, Typography, Tooltip } from '@mui/material';
import { NODE_UI, stopPropagation } from './nodeUiStyles';
import {
  EMPTY_FIELD_MSG,
  NO_SPECIAL_CHARS_MSG,
  formatPlaceholderKey,
  getDisplayPlaceholderKey,
  hasInvalidPlaceholderKeyChars,
} from './placeholderKeyUtils';

/**
 * NodePlaceholderTextField — MUI TextField with ${...} adornments,
 * auto-sanitization (alphanumeric + underscore + colon), and error tooltip.
 *
 * Used for defining output placeholder keys (e.g., ${email_subject}).
 *
 * Usage:
 *   <NodePlaceholderTextField
 *     label="Placeholder Key"
 *     value={placeholderKey}
 *     onChange={(val) => setPlaceholderKey(val)}
 *     error={!placeholderKey}
 *     helperText="Required"
 *   />
 */

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontSize: NODE_UI.fontSize.value,
    borderRadius: NODE_UI.borderRadiusSm,
    backgroundColor: theme.palette.background.default,
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.text.secondary,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.text.primary,
      borderWidth: '1px',
    },
  },
  '& .MuiInputBase-input': {
    padding: NODE_UI.input.padding,
    fontSize: NODE_UI.fontSize.value,
  },
  '& .MuiInputAdornment-root': {
    fontSize: NODE_UI.fontSize.value,
    color: theme.palette.text.secondary,
  },
}));

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

const NodePlaceholderTextField = React.memo(function NodePlaceholderTextField({
  label,
  value = '',
  defaultValue,
  onChange,
  onBlur,
  error: externalError,
  helperText,
  placeholder,
  disabled = false,
  fullWidth = true,
  disableAdornment = false,
  excludePrefix = '',
  sx,
}) {
  const [localError, setLocalError] = useState('');
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const displayValue = getDisplayPlaceholderKey(value, defaultValue, excludePrefix);

  const hasError = !displayValue || !!localError || !!externalError;
  const errorMessage = !displayValue
    ? EMPTY_FIELD_MSG
    : localError || helperText || '';

  const handleChange = useCallback((e) => {
    const raw = e.target.value;
    const containsInvalid = hasInvalidPlaceholderKeyChars(raw);

    if (containsInvalid) {
      setLocalError(NO_SPECIAL_CHARS_MSG);
      setTooltipOpen(true);
    } else {
      setLocalError('');
    }

    onChange?.(formatPlaceholderKey(raw, excludePrefix));
  }, [excludePrefix, onChange]);

  const handleBlur = useCallback((e) => {
    setTooltipOpen(false);
    onBlur?.(e);
  }, [onBlur]);

  const handleFocus = useCallback(() => {
    if (hasError && errorMessage) {
      setTooltipOpen(true);
    }
  }, [hasError, errorMessage]);

  return (
    <div onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation}>
      {label && <Label>{label}</Label>}
      <Tooltip
        title={errorMessage}
        arrow
        placement="top"
        open={tooltipOpen && hasError && !!errorMessage}
        disableFocusListener
        disableHoverListener
        disableTouchListener
      >
        <StyledTextField
          size="small"
          variant="outlined"
          fullWidth={fullWidth}
          disabled={disabled}
          value={displayValue}
          error={hasError}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={placeholder}
          sx={sx}
          InputProps={{
            startAdornment: !disableAdornment && (
              <InputAdornment position="start">{'${'}</InputAdornment>
            ),
            endAdornment: !disableAdornment && (
              <InputAdornment position="end">{'}'}</InputAdornment>
            ),
          }}
        />
      </Tooltip>
    </div>
  );
});

export default NodePlaceholderTextField;
